import { useEffect, useRef, useState, useCallback, ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { NarrativeMeta } from './CameraController';
import { AlarmCodes } from '../../store/detailedSimulation';

const AUTO_HIDE_MS = 10000;
const ALARM_HIDE_MS = 25000;

const TTS_URL = import.meta.env.DEV ? 'http://localhost:5100/tts' : '/tts';

let ttsEnabled = false;

export function isTTSEnabled() { return ttsEnabled; }
export function setTTSEnabled(v: boolean) { ttsEnabled = v; }

/** Expand abbreviations for natural TTS reading */
function expandForTTS(raw: string): string {
  return raw
    .replace(/[*!]/g, '')
    .replace(/C\//g, 'Calle')
    .replace(/Av\./g, 'Avenida')
    .replace(/Glta\./g, 'Glorieta')
    .replace(/R(\d)/g, 'Regulador $1')
    .replace(/P(\d)/g, 'Plan $1')
    .replace(/(\d+)s\b/g, '$1 segundos');
}

let currentAudio: HTMLAudioElement | null = null;
let currentAbort: AbortController | null = null;

/** Strip markup, expand abbreviations, send to TTS, play result (cancels previous) */
export function speakNarrative(raw: string, isAlarm = false) {
  if (!ttsEnabled) return;

  // Cancel previous: abort in-flight fetch + stop playing audio
  if (currentAbort) currentAbort.abort();
  if (currentAudio) { currentAudio.pause(); currentAudio = null; }

  const abort = new AbortController();
  currentAbort = abort;

  const text = expandForTTS(raw);
  fetch(TTS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      text: isAlarm ? `Atención: ${text}` : text,
    }),
    signal: abort.signal,
  })
    .then(r => {
      if (!r.ok) throw new Error(`TTS ${r.status}`);
      return r.blob();
    })
    .then(blob => {
      if (abort.signal.aborted) return;
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => { URL.revokeObjectURL(url); currentAudio = null; };
      currentAudio = audio;
      audio.play().catch(() => {});
    })
    .catch(() => {}); // Silent fail — TTS is best-effort
}

/**
 * Parse simple markup in narrative text:
 *   *text*  → green highlight
 *   !text!  → red highlight
 */
function parseNarrative(raw: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const regex = /(\*[^*]+\*|![^!]+!)/g;
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(raw)) !== null) {
    if (match.index > last) {
      parts.push(raw.slice(last, match.index));
    }
    const content = match[0].slice(1, -1);
    const color = match[0][0] === '*' ? 'text-green-400' : 'text-red-400';
    parts.push(
      <span key={match.index} className={`${color} font-semibold`}>{content}</span>
    );
    last = regex.lastIndex;
  }
  if (last < raw.length) parts.push(raw.slice(last));
  return parts;
}

/** Info narrative — top center */
export function NarrativeOverlay({ meta }: { meta: NarrativeMeta | null }) {
  const [visible, setVisible] = useState(false);
  const [display, setDisplay] = useState<NarrativeMeta | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (meta) {
      setDisplay(meta);
      setVisible(true);
      clearTimeout(timerRef.current);
      const hideMs = meta.narrativeDuration ?? (meta.alarm ? ALARM_HIDE_MS : AUTO_HIDE_MS);
      timerRef.current = setTimeout(() => setVisible(false), hideMs);
      speakNarrative(meta.text, !!meta.alarm);
    } else {
      setVisible(false);
    }
    return () => clearTimeout(timerRef.current);
  }, [meta]);

  return (
    <AnimatePresence>
      {visible && display && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.4 }}
          className="absolute top-[3%] left-0 right-0 z-20 flex justify-center pointer-events-none px-3 sm:pr-[220px] lg:pr-4"
        >
          <div className={`bg-gray-900/95 backdrop-blur-xl border rounded-xl px-4 py-2 sm:px-6 sm:py-2.5 text-sm sm:text-base lg:text-xl text-gray-50 shadow-2xl max-w-[90vw] ${display.alarm ? 'border-red-500/50' : 'border-gray-600/40'}`}>
            <span className={`inline-block text-xs font-bold uppercase tracking-wide mr-3 px-2 py-0.5 rounded ${display.alarm ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}`}>
              {display.alarm ? 'Alarma' : 'Info'}
            </span>
            {parseNarrative(display.text)}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface AlarmEntry {
  id: number;
  meta: NarrativeMeta;
}

let alarmSeq = 0;

const REG_TEXT_COLOR: Record<number, string> = {
  1: 'text-cyan-400',
  2: 'text-amber-400',
  3: 'text-emerald-400',
  4: 'text-violet-400',
};

const REG_DOT_COLOR: Record<number, string> = {
  1: 'bg-cyan-400',
  2: 'bg-amber-400',
  3: 'bg-emerald-400',
  4: 'bg-violet-400',
};

const REG_LABELS: Record<number, string> = {
  1: 'Plaza Mayor',
  2: 'Av. Ejército',
  3: 'Glorieta Boixareu',
  4: 'C/ del Trabajo',
};

const ALARM_SEVERITY_LABEL: Record<string, string> = {
  C: 'CRÍTICO', I: 'CRÍTICO', E: 'CRÍTICO',
  A: 'AVISO', B: 'AVISO', J: 'AVISO', L: 'AVISO', V: 'AVISO', N: 'AVISO',
};

/** Alarm toast stack — FIFO, up to 2 visible, slides up when oldest expires */
export function AlarmOverlay({ meta }: { meta: NarrativeMeta | null }) {
  const [entries, setEntries] = useState<AlarmEntry[]>([]);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const removeEntry = useCallback((id: number) => {
    setEntries(prev => prev.filter(e => e.id !== id));
    timersRef.current.delete(id);
  }, []);

  useEffect(() => {
    if (!meta?.alarm) return;
    const id = ++alarmSeq;
    const entry: AlarmEntry = { id, meta };

    setEntries(prev => {
      const next = [...prev, entry];
      // Keep max 2: drop oldest if overflow
      if (next.length > 2) {
        const removed = next.shift()!;
        const timer = timersRef.current.get(removed.id);
        if (timer) clearTimeout(timer);
        timersRef.current.delete(removed.id);
      }
      return next;
    });

    timersRef.current.set(id, setTimeout(() => removeEntry(id), ALARM_HIDE_MS));
  }, [meta, removeEntry]);

  // Cleanup all timers on unmount only
  useEffect(() => {
    const timers = timersRef.current;
    return () => { for (const t of timers.values()) clearTimeout(t); };
  }, []);

  return (
    <div className="absolute top-[14%] left-3 z-40 pointer-events-none flex flex-col gap-2">
      <AnimatePresence>
        {entries.map(entry => (
          <motion.div
            key={entry.id}
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            layout
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <div className="bg-gray-900/90 backdrop-blur-md border border-red-500/50 rounded-lg shadow-2xl overflow-hidden min-w-[220px]">
              <div className="flex items-center gap-2 px-3 py-1.5">
                <div className={`w-2 h-2 rounded-full animate-pulse flex-shrink-0 ${REG_DOT_COLOR[entry.meta.alarm!.driverId] || 'bg-red-500'}`} />
                <span className={`text-sm font-mono font-bold ${REG_TEXT_COLOR[entry.meta.alarm!.driverId] || 'text-white'}`}>
                  R{entry.meta.alarm!.driverId}
                </span>
                <span className="text-xs font-medium text-red-400 bg-red-500/15 px-1.5 py-0.5 rounded">
                  {ALARM_SEVERITY_LABEL[entry.meta.alarm!.code] || 'AVISO'}
                </span>
                <span className="text-[10px] text-gray-500 font-mono ml-auto">
                  {new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              </div>
              <div className="px-3 pb-2 pt-0.5">
                <span className="text-sm text-gray-200">
                  {AlarmCodes[entry.meta.alarm!.code as keyof typeof AlarmCodes] || entry.meta.alarm!.code}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
